import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react'
import { useAuth } from 'headlo-auth'
import { useDpopFetch } from './useDpopFetch'

// WhoAmIContext — one fetch of /v1/whoami per sign-in, shared to every
// consumer (ShopContext for merchant.shops, future affiliate/agency
// consumers). Server-side KV-caches for 60s; client-side we hold it in
// context for the whole session and refresh on demand.
//
// Design rationale: ShopContext used to hit /v1/mobile/shops directly.
// With the unified /v1/whoami endpoint we route the same shop data through
// this context instead — plus it's the natural place to hang "am I an
// affiliate" and "am I an agency user" checks that gate future UI.

const API_URL = (import.meta.env.VITE_HEADLO_API_URL as string | undefined) ?? 'https://api.headlo.com'

export interface WhoAmIShop {
  handle:      string
  brand_name:  string | null
  role:        'owner' | 'collaborator'
  has_stripe:  boolean
  has_shopify: boolean
}

export interface WhoAmI {
  user: {
    sub:            string
    source:         'headlo' | 'clerk'
    email:          string | null
    email_verified: boolean
  }
  merchant: {
    shops: WhoAmIShop[]
  }
  affiliate: {
    is_affiliate: boolean
    status:       string | null
    handle:       string | null
  }
  agency: {
    agency_id:    string | null
    name:         string | null
    account_type: 'agency' | 'personal' | null
    plan:         string | null
    role:         string | null
  }
}

export type WhoAmIStatus =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; whoami: WhoAmI }

interface WhoAmIContextValue {
  state:   WhoAmIStatus
  refresh: () => void
}

const WhoAmICtx = createContext<WhoAmIContextValue | null>(null)

export function WhoAmIProvider({ children }: { children: ReactNode }) {
  const { isLoaded, isSignedIn } = useAuth()
  const authFetch = useDpopFetch()
  const [state, setState] = useState<WhoAmIStatus>({ status: 'loading' })
  const [reloadTick, setReloadTick] = useState(0)

  const refresh = useCallback(() => setReloadTick(t => t + 1), [])

  useEffect(() => {
    if (!isLoaded) return
    if (!isSignedIn) {
      setState({ status: 'loading' })
      return
    }

    let alive = true
    setState({ status: 'loading' })

    void (async () => {
      try {
        const res = await authFetch(`${API_URL.replace(/\/$/, '')}/v1/whoami`)
        if (!alive) return
        if (!res.ok) {
          setState({ status: 'error', message: `${res.status} ${res.statusText}` })
          return
        }
        const body = await res.json() as WhoAmI
        setState({ status: 'ready', whoami: body })
      } catch (e) {
        if (!alive) return
        setState({ status: 'error', message: (e as Error).message ?? String(e) })
      }
    })()

    return () => { alive = false }
  }, [isLoaded, isSignedIn, authFetch, reloadTick])

  return <WhoAmICtx.Provider value={{ state, refresh }}>{children}</WhoAmICtx.Provider>
}

export function useWhoAmI(): WhoAmIContextValue {
  const ctx = useContext(WhoAmICtx)
  if (!ctx) throw new Error('useWhoAmI must be used inside <WhoAmIProvider>')
  return ctx
}
